// Central constants for the Vendor / Third-Party Risk Management module.

export const RISK_LEVELS = {
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
  UNSCORED: "Unscored",
};

export const RISK_LEVEL_ORDER = [
  RISK_LEVELS.CRITICAL,
  RISK_LEVELS.HIGH,
  RISK_LEVELS.MEDIUM,
  RISK_LEVELS.LOW,
  RISK_LEVELS.UNSCORED,
];

export const ASSESSMENT_TYPES = [
  "Security",
  "Privacy",
  "Legal",
  "AI",
  "Finance",
  "Custom",
];

export const ASSESSMENT_STATUS = {
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  IN_REVIEW: "In Review",
  COMPLETED: "Completed",
};

export const SECURITY_REVIEW_STATUS = {
  DRAFT: "Draft",
  COMPLETED: "Completed",
  DISCARDED: "Discarded",
};

export const RECOMMENDATIONS = {
  APPROVED: "Approved",
  CONDITIONALLY_APPROVED: "Conditionally approved",
  NOT_APPROVED: "Not approved",
};

export const DECISIONS = {
  APPROVE: "Approved",
  APPROVE_WITH_EXCEPTIONS: "Approved with exceptions",
  REJECT: "Rejected",
};

export const REVIEW_STATES = {
  UPCOMING: "Upcoming",
  DUE_SOON: "Due soon",
  DUE: "Due",
  OVERDUE: "Overdue",
  COMPLETED: "Completed",
};

export const formatDecisionLabel = (decision) => {
  if (!decision) return "";
  if (decision === "Approve") return DECISIONS.APPROVE;
  if (decision === "Approve with exceptions") return DECISIONS.APPROVE_WITH_EXCEPTIONS;
  if (decision === "Reject") return DECISIONS.REJECT;
  return decision;
};

export const getDisplayedReviewStatus = (vendor) => {
  if (vendor?.reviewState === REVIEW_STATES.COMPLETED && vendor?.decision) {
    return formatDecisionLabel(vendor.decision);
  }
  return vendor?.reviewState || "";
};

export const VENDOR_SOURCES = {
  MANUAL: "Manual",
  DISCOVERED: "Discovered",
};

export const VENDOR_CATEGORIES = [
  "Cloud Infrastructure",
  "Identity & Access",
  "Communication",
  "Analytics",
  "Payments",
  "HR & Payroll",
  "Marketing",
  "Security",
  "Productivity",
  "AI / ML",
  "Other",
];

export const AUTH_TYPES = ["SSO / SAML", "OAuth", "Username & Password", "API Key", "SCIM"];

export const FINDING_SEVERITY = {
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};

export const FINDING_STATUS = {
  OPEN: "Open",
  IN_REMEDIATION: "In Remediation",
  ACCEPTED: "Risk Accepted",
  CLOSED: "Closed",
};

export const EVIDENCE_TYPES = [
  "SOC 2 Type II",
  "ISO 27001",
  "Penetration Test",
  "Security Policy",
  "DPA",
  "Trust Center",
  "Other",
];

export const CRITERIA_RESULTS = {
  PASS: "Pass",
  FAIL: "Fail",
  PARTIAL: "Partial",
  NOT_ASSESSED: "Not Assessed",
};

// Inherent-risk scoring inputs. Each option carries a weight used by riskScoring.js.
export const RISK_FACTORS = [
  {
    key: "dataSensitivity",
    label: "Data Sensitivity",
    helper: "Most sensitive data the vendor can access",
    options: [
      { value: "none", label: "No data", weight: 0 },
      { value: "public", label: "Public / non-sensitive", weight: 1 },
      { value: "internal", label: "Internal / confidential", weight: 2 },
      { value: "pii", label: "PII / customer data", weight: 3 },
      { value: "regulated", label: "Regulated (PHI / PCI / financial)", weight: 4 },
    ],
  },
  {
    key: "dataVolume",
    label: "Data Volume",
    helper: "How much data the vendor processes",
    options: [
      { value: "none", label: "None", weight: 0 },
      { value: "low", label: "Low", weight: 1 },
      { value: "medium", label: "Medium", weight: 2 },
      { value: "high", label: "High / bulk", weight: 3 },
    ],
  },
  {
    key: "environmentAccess",
    label: "Environment Access",
    helper: "Level of access to your systems",
    options: [
      { value: "none", label: "No access", weight: 0 },
      { value: "limited", label: "Limited / read-only", weight: 2 },
      { value: "standard", label: "Standard access", weight: 3 },
      { value: "privileged", label: "Privileged / production", weight: 4 },
    ],
  },
  {
    key: "operationalImpact",
    label: "Operational Impact",
    helper: "Impact if the vendor goes down",
    options: [
      { value: "low", label: "Low", weight: 1 },
      { value: "medium", label: "Medium", weight: 2 },
      { value: "high", label: "High", weight: 3 },
      { value: "critical", label: "Business-critical", weight: 4 },
    ],
  },
  {
    key: "businessImpact",
    label: "Business Impact",
    helper: "Financial / reputational exposure",
    options: [
      { value: "low", label: "Low", weight: 1 },
      { value: "medium", label: "Medium", weight: 2 },
      { value: "high", label: "High", weight: 3 },
      { value: "severe", label: "Severe", weight: 4 },
    ],
  },
];
